import { useEffect, useMemo } from 'react';

import Helmet from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Grid, GridColumn } from '@atlaskit/page';
import { Box, Inline } from '@atlaskit/primitives';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';

import { FocusedPageHeader } from 'src/components/accessibility';
import { BetaLozenge } from 'src/components/lozenges';
import {
  FeedbackCollector,
  FEEDBACK_CONTEXT_LABELS,
} from 'src/components/pipelines/components/feedback-collector';
import {
  getCapabilities,
  getRepository,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { getPages } from 'src/components/pipelines/utils/pagination';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useParams } from 'src/router/hooks';
import Breadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import { getUpToSmallBreakpointState } from 'src/selectors/global-selectors';

import {
  MergeQueueDetails,
  MergeQueuePicker,
  MergeQueuePullRequestsTable,
  MergeQueuesEmpty,
  MergeQueueInactive,
  MergeQueuesLoading,
  MergeQueuesPipelinesDisabled,
  MergeQueuesUnavailable,
  HowToUseMergeQueuesLink,
} from './components';
import { useMergeQueuePipelineDefinitions } from './hooks';
import { messages } from './merge-queues.i18n';
import { MergeQueueActionModal, MergeQueueCreateEditModal } from './modals';
import {
  useFocusedMergeQueue,
  useIsEmpty,
  useMergeQueuesActions,
} from './provider';
import { MergeQueuesProps, MergeQueueState } from './types';
import { MERGE_QUEUES_PAGE_LENGTH } from './utils';

export const MergeQueues = ({
  mergeQueues,
  mergeQueuesLoading,
}: MergeQueuesProps) => {
  const { formatMessage } = useIntl();
  const { mergeQueueUuid } = useParams<{ mergeQueueUuid?: string }>();
  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const isUpToSmallBreakpointActive = useSelector(getUpToSmallBreakpointState);
  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const {
    loading: mergeQueuePipelineDefinitionsLoading,
    isPipelineDefinedForMergeQueue,
  } = useMergeQueuePipelineDefinitions(
    repository,
    focusedMergeQueue ?? undefined
  );
  const [{ isEmpty }] = useIsEmpty();
  const [
    ,
    {
      setFocusedMergeQueue,
      setIsEmpty,
      setIsLoading,
      setMergeQueues,
      setMergeQueuesPages,
    },
  ] = useMergeQueuesActions();

  useEffect(
    function setMergeQueuesState() {
      setIsLoading(mergeQueuesLoading);

      if (!mergeQueuesLoading) {
        setMergeQueues(mergeQueues?.values ?? []);
        setMergeQueuesPages(
          getPages(mergeQueues?.size ?? 0, MERGE_QUEUES_PAGE_LENGTH)
        );
        setIsEmpty(mergeQueues?.values?.length === 0);
        if (mergeQueueUuid) {
          setFocusedMergeQueue(
            mergeQueues?.values.find(mq => mq.uuid === mergeQueueUuid)
          );
        } else {
          setFocusedMergeQueue(undefined);
        }
      }
    },
    [
      mergeQueueUuid,
      mergeQueuesLoading,
      mergeQueues,
      setFocusedMergeQueue,
      setIsEmpty,
      setIsLoading,
      setMergeQueues,
      setMergeQueuesPages,
    ]
  );

  const isPaidOrPremium =
    capabilities.hasFetchedCapabilities && capabilities.isPaidOrPremium;
  const isPipelinesEnabled =
    capabilities.hasFetchedCapabilities && capabilities.pipelinesEnabled;

  const getContent = useMemo(() => {
    if (
      !capabilities.hasFetchedCapabilities ||
      mergeQueuesLoading ||
      mergeQueuePipelineDefinitionsLoading
    ) {
      return <MergeQueuesLoading />;
    }
    if (!isPaidOrPremium) {
      return <MergeQueuesUnavailable />;
    }
    if (!isPipelinesEnabled) {
      return <MergeQueuesPipelinesDisabled />;
    }
    if (isEmpty) {
      return <MergeQueuesEmpty />;
    }
    return (
      <>
        <Box paddingBlockStart="space.100" paddingBlockEnd="space.300">
          <MergeQueuePicker />
        </Box>
        {focusedMergeQueue && (
          <>
            <MergeQueueDetails />
            {focusedMergeQueue.state === MergeQueueState.INACTIVE &&
            !isPipelineDefinedForMergeQueue ? (
              <MergeQueueInactive />
            ) : (
              <MergeQueuePullRequestsTable />
            )}
          </>
        )}
      </>
    );
  }, [
    capabilities,
    mergeQueuesLoading,
    isPaidOrPremium,
    isPipelinesEnabled,
    isEmpty,
    focusedMergeQueue,
    mergeQueuePipelineDefinitionsLoading,
    isPipelineDefinedForMergeQueue,
  ]);

  const isMergeQueuesEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesEnabled
  );

  if (!isMergeQueuesEnabled) return null;

  return (
    <ContextualAnalyticsData sourceName="mergeQueues" sourceType={SCREEN}>
      <FireScreenAnalytics attributes={{ repositoryUuid: repository.uuid }} />
      <Helmet defer={false} title={formatMessage(messages.mergeQueuesTitle)} />
      <Grid
        layout="fluid"
        spacing={isUpToSmallBreakpointActive ? 'cosy' : 'comfortable'}
      >
        <GridColumn>
          <FocusedPageHeader
            breadcrumbs={<Breadcrumbs />}
            actions={
              <FeedbackCollector
                feedbackContextLabel={FEEDBACK_CONTEXT_LABELS.MERGE_QUEUES}
              />
            }
          >
            <Inline space="space.100" alignBlock="center" alignInline="start">
              {formatMessage(messages.mergeQueuesTitle)}
              <BetaLozenge />
            </Inline>
          </FocusedPageHeader>
          <>
            <FormattedMessage
              {...messages.introMsg}
              tagName="p"
              values={{
                learnMore: <HowToUseMergeQueuesLink />,
              }}
            />
            {getContent}
            <MergeQueueActionModal />
            <MergeQueueCreateEditModal />
          </>
        </GridColumn>
      </Grid>
    </ContextualAnalyticsData>
  );
};
